import React, { useState, useEffect } from 'react';
import { Search, Bell, Package, Users, FileText, Database, ShoppingCart, CreditCard, BarChart3, Calendar, Shield, Settings, LogOut } from 'lucide-react';
import luniaCoreLogo from '../assets/LUNIA_CORE.png';
import { Dashboard } from './Dashboard';
import { Patients } from './Patients';
import { Prescriptions } from './Prescriptions';
import { Inventory } from './Inventory';
import { Ordering } from './Ordering';
import { Billing } from './Billing';
import { Reports } from './Reports';
import { CalendarPage } from './Calendar';
import { Compliance as CompliancePage } from './Compliance';
import { SettingsPage } from './Settings';

interface LayoutProps {
  onLogout: () => void;
  user: { username: string; password: string } | null;
}

export function Layout({ onLogout, user }: LayoutProps) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Package, description: 'Overview & Analytics' },
    { id: 'patients', label: 'Patients', icon: Users, description: 'Patient Management' },
    { id: 'prescriptions', label: 'Prescriptions', icon: FileText, description: 'Rx Processing' },
    { id: 'inventory', label: 'Inventory', icon: Database, description: 'Stock Management' },
    { id: 'ordering', label: 'Ordering', icon: ShoppingCart, description: 'Procurement' },
    { id: 'billing', label: 'Billing', icon: CreditCard, description: 'Insurance & Claims' },
    { id: 'reports', label: 'Reports', icon: BarChart3, description: 'Analytics' },
    { id: 'calendar', label: 'Calendar', icon: Calendar, description: 'Scheduling' },
    { id: 'compliance', label: 'Compliance', icon: Shield, description: 'Safety & Regulations' },
    { id: 'settings', label: 'Settings', icon: Settings, description: 'System Configuration' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="relative bg-white/80 backdrop-blur-xl shadow-2xl border-b border-gray-200/50 h-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#6B38FB]/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#4B0082]/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#6B38FB]/3 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
          {/* Logo Section */}
          <div className="flex items-center space-x-6">
            <div className="group relative">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4B0082] to-[#6B38FB] rounded-3xl border-2 border-[#6B38FB]/30 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 flex items-center justify-center">
                <img src={luniaCoreLogo} alt="Lunia Core" className="w-10 h-10 drop-shadow-2xl" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-[#6B38FB] to-[#4B0082] rounded-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
            </div>
            
            <div>
              <h1 className="text-2xl font-black bg-gradient-to-r from-[#4B0082] to-[#6B38FB] bg-clip-text text-transparent">
                LUNIA CORE
              </h1>
              <p className="text-sm text-gray-600 font-medium">Pharmacy Management System</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex-1 max-w-2xl mx-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search patients, prescriptions, inventory..."
                className="w-72 pl-12 pr-6 py-3 bg-white/80 backdrop-blur-sm border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300 hover:bg-white shadow-lg hover:shadow-xl"
              />
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <button className="relative p-3 bg-white/80 backdrop-blur-sm rounded-2xl border-2 border-gray-200 hover:border-[#6B38FB]/30 hover:bg-white transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
              <Bell className="w-6 h-6 text-gray-600" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-bold">3</span>
            </button>

            {/* User Menu */}
            <div className="relative group">
              <button className="flex items-center space-x-3 p-3 bg-gradient-to-r from-[#4B0082]/10 to-[#6B38FB]/10 backdrop-blur-sm rounded-2xl border-2 border-[#6B38FB]/20 hover:border-[#6B38FB]/40 hover:bg-gradient-to-r hover:from-[#4B0082]/20 hover:to-[#6B38FB]/20 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
                <div className="w-8 h-8 bg-gradient-to-br from-[#4B0082] to-[#6B38FB] rounded-xl flex items-center justify-center text-white font-bold text-sm">
                  {user?.username?.charAt(0).toUpperCase() || 'A'}
                </div>
                <span className="text-gray-700 font-semibold">{user?.username || 'Admin'}</span>
              </button>
              
              {/* Dropdown Menu */}
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-2xl border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-50">
                <div className="p-2">
                  <button
                    onClick={onLogout}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-red-50 hover:text-red-700 rounded-xl transition-all duration-200 group"
                  >
                    <LogOut className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-2 py-4 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex flex-col items-center space-y-1 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                    isActive
                      ? 'bg-gradient-to-r from-[#4B0082] to-[#6B38FB] text-white shadow-lg shadow-[#6B38FB]/25'
                      : 'text-gray-600 hover:text-[#6B38FB] hover:bg-[#6B38FB]/5'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                  <span className="text-sm font-semibold">{tab.label}</span>
                  <span className={`text-xs ${isActive ? 'text-purple-100' : 'text-gray-400'}`}>
                    {tab.description}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'dashboard' && <Dashboard />}
        {activeTab === 'patients' && <Patients />}
        {activeTab === 'prescriptions' && <Prescriptions />}
        {activeTab === 'inventory' && <Inventory />}
        {activeTab === 'ordering' && <Ordering />}
        {activeTab === 'billing' && <Billing />}
        {activeTab === 'reports' && <Reports />}
        {activeTab === 'calendar' && <CalendarPage />}
        {activeTab === 'compliance' && <CompliancePage />}
        {activeTab === 'settings' && <SettingsPage />}
      </main>

      {/* Floating Action Button */}
      <button className="fixed bottom-8 right-8 w-16 h-16 bg-gradient-to-r from-[#4B0082] to-[#6B38FB] text-white rounded-full shadow-2xl hover:shadow-[#6B38FB]/25 transition-all duration-300 hover:scale-110 hover:rotate-12 flex items-center justify-center group">
        <Package className="w-8 h-8 group-hover:scale-110 transition-transform duration-200" />
      </button>
    </div>
  );
}
